// src/components/ProductCard.jsx
import React, { useState } from 'react';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product }) => {
  const [selectedOption, setSelectedOption] = useState(product.priceOptions?.[0] || null);
  const [isAdding, setIsAdding] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { addToCart } = useCart();

  const handleAddToCart = async () => {
    setIsAdding(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (product.priceOptions) {
      addToCart(product, selectedOption);
    } else {
      addToCart(product);
    }
    
    setIsAdding(false);
  };

  const getStrainColor = (strain) => {
    switch(strain?.toLowerCase()) {
      case 'sativa': return { 
        background: 'linear-gradient(135deg, #fef9c3 0%, #fde047 100%)', 
        color: '#854d0e',
        emoji: '☀️',
        icon: '🌞'
      };
      case 'indica': return { 
        background: 'linear-gradient(135deg, #e9d5ff 0%, #c084fc 100%)', 
        color: '#6b21a8',
        emoji: '🌙',
        icon: '🌜'
      };
      case 'hybrid': return { 
        background: 'linear-gradient(135deg, #d1fae5 0%, #6ee7b7 100%)', 
        color: '#065f46',
        emoji: '⚡',
        icon: '⚡'
      };
      default: return { 
        background: 'linear-gradient(135deg, #e4e4e7 0%, #d4d4d8 100%)', 
        color: '#3f3f46',
        emoji: '🌿',
        icon: '🍃'
      };
    }
  };

  const strainStyle = getStrainColor(product.strain);

  const formatCategory = (category) => {
    return category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPlaceholderEmoji = () => {
    const category = product.category?.toLowerCase() || '';
    if (category.includes('flower')) return '🌿';
    if (category.includes('exotic')) return '👑';
    if (category.includes('edible')) return '🍪';
    if (category.includes('vape') || category.includes('cart')) return '💨';
    if (category.includes('concentrate')) return '🔥';
    if (category.includes('preroll')) return '📦';
    if (category.includes('tincture')) return '💧';
    return '🏰';
  };

  return (
    <div 
      className="pot-castle-product-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="pot-castle-product-image">
        <div className="pot-castle-image-content">
          {product.image && !imageError ? (
            <img 
              src={product.image} 
              alt={product.name}
              className="pot-castle-product-img"
              loading="lazy"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="pot-castle-placeholder-emoji">
              {getPlaceholderEmoji()}
            </span>
          )}
        </div>

        {/* Badges Container */}
        <div className="pot-castle-badges">
          <div className="pot-castle-badges-left">
            {product.isNew && (
              <span className="pot-castle-badge pot-castle-badge-new">
                🔥 New
              </span>
            )}
            
            {product.strain && (
              <span 
                className="pot-castle-badge pot-castle-badge-strain"
                style={{
                  background: strainStyle.background,
                  color: strainStyle.color
                }}
              >
                <span>{strainStyle.icon}</span>
                <span>{product.strain}</span>
              </span>
            )}
          </div>

          <div className="pot-castle-badges-right">
            {product.popular && (
              <span className="pot-castle-badge pot-castle-badge-popular">
                ⭐ Popular
              </span>
            )}
            
            {product.discount && (
              <span className="pot-castle-badge pot-castle-badge-discount">
                -{product.discount}%
              </span>
            )}
          </div>
        </div>

        {/* Quick View Overlay */}
        {isHovered && (
          <div className="pot-castle-quick-view">
            <button className="pot-castle-quick-view-btn">
              Quick View
            </button>
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className="pot-castle-product-content">
        {/* Category & Rating */}
        <div className="pot-castle-product-header">
          <span className="pot-castle-category">
            {formatCategory(product.category)}
          </span>

          {product.rating && (
            <div className="pot-castle-rating">
              <span className="pot-castle-star">★</span>
              <span className="pot-castle-rating-value">
                {product.rating}
              </span>
            </div>
          )}
        </div>

        {/* Name */}
        <h3 className="pot-castle-product-name">
          {product.name}
        </h3>

        {/* Description */}
        <p className="pot-castle-product-description">
          {product.description}
        </p>

        {/* Key Info Chips */}
        <div className="pot-castle-product-chips">
          {product.thcContent && (
            <span className="pot-castle-chip pot-castle-chip-thc">
              <span>⚡</span>
              THC: {product.thcContent}mg
            </span>
          )}
          
          {product.weight && (
            <span className="pot-castle-chip pot-castle-chip-weight">
              <span>⚖️</span>
              {product.weight}
            </span>
          )}
        </div>

        {/* Price Options */}
        {product.priceOptions && product.priceOptions.length > 0 && (
          <div className="pot-castle-price-options">
            <div className="pot-castle-options-grid">
              {product.priceOptions.slice(0, 3).map((opt) => (
                <button
                  key={opt.option}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOption(opt);
                  }}
                  className={`pot-castle-option-btn ${selectedOption?.option === opt.option ? 'active' : ''}`}
                >
                  <span>{opt.option}</span>
                  <span className="pot-castle-option-price">
                    ${opt.price}
                  </span>
                </button>
              ))}
            </div>
            
            {product.priceOptions.length > 3 && (
              <span className="pot-castle-more-options">
                +{product.priceOptions.length - 3} more options
              </span>
            )}
          </div>
        )}

        {/* Price and Add to Cart */}
        <div className="pot-castle-product-footer">
          <div className="pot-castle-price-section">
            <div className="pot-castle-price">
              ${product.priceOptions ? selectedOption?.price : product.price}
            </div>
            {product.priceOptions && selectedOption && (
              <div className="pot-castle-price-option">
                {selectedOption.option}
              </div>
            )}
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
            disabled={isAdding}
            className={`pot-castle-add-btn ${isAdding ? 'adding' : ''}`}
          >
            {isAdding ? (
              <>
                <div className="pot-castle-spinner" />
                <span>Adding</span>
              </>
            ) : (
              <>
                <span>🛒</span>
                <span>Add to Cart</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        /* Product Card Container */
        .pot-castle-product-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--card, #ffffff);
          border-radius: 1rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          cursor: pointer;
          border: 1px solid rgba(139, 92, 246, 0.1);
        }

        .pot-castle-product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border-color: rgba(139, 92, 246, 0.3);
        }

        /* Image Container */
        .pot-castle-product-image {
          position: relative;
          padding-top: 75%;
          overflow: hidden;
          background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
          flex-shrink: 0;
        }

        .pot-castle-image-content {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pot-castle-product-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.3s ease;
        }

        .pot-castle-product-card:hover .pot-castle-product-img {
          transform: scale(1.05);
        }

        .pot-castle-placeholder-emoji {
          font-size: clamp(3rem, 10vw, 5rem);
          transition: transform 0.3s ease;
        }

        .pot-castle-product-card:hover .pot-castle-placeholder-emoji {
          transform: scale(1.1);
        }

        /* Badges */
        .pot-castle-badges {
          position: absolute;
          top: clamp(8px, 2vw, 12px);
          left: clamp(8px, 2vw, 12px);
          right: clamp(8px, 2vw, 12px);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          z-index: 2;
          pointer-events: none;
          flex-wrap: wrap;
          gap: 8px;
        }

        .pot-castle-badges-left,
        .pot-castle-badges-right {
          display: flex;
          flex-direction: column;
          gap: clamp(4px, 1vw, 8px);
        }

        .pot-castle-badges-left {
          align-items: flex-start;
        }

        .pot-castle-badges-right {
          align-items: flex-end;
        }

        .pot-castle-badge {
          padding: clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px);
          border-radius: 20px;
          font-size: clamp(0.6rem, 1.5vw, 0.75rem);
          font-weight: 700;
          line-height: 1;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pot-castle-badge-new {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .pot-castle-badge-popular {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .pot-castle-badge-discount {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        /* Quick View */
        .pot-castle-quick-view {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
          padding: 20px 12px 12px;
          transform: translateY(0);
          transition: transform 0.3s ease;
          z-index: 3;
        }

        .pot-castle-quick-view-btn {
          width: 100%;
          padding: 8px;
          background: linear-gradient(135deg, #8b5cf6, #ec489a);
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pot-castle-quick-view-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
        }

        /* Content */
        .pot-castle-product-content {
          padding: clamp(12px, 3vw, 20px);
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          gap: clamp(8px, 2vw, 12px);
        }

        /* Header */
        .pot-castle-product-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .pot-castle-category {
          font-size: clamp(0.65rem, 1.5vw, 0.75rem);
          color: #8b5cf6;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: rgba(139, 92, 246, 0.1);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .pot-castle-rating {
          display: flex;
          align-items: center;
          gap: 2px;
          background: rgba(0, 0, 0, 0.05);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .pot-castle-star {
          color: #fbbf24;
          font-size: 0.875rem;
        }

        .pot-castle-rating-value {
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          font-weight: 600;
          color: var(--text, #18181b);
        }

        /* Product Name */
        .pot-castle-product-name {
          font-size: clamp(1rem, 3vw, 1.125rem);
          font-weight: 700;
          line-height: 1.3;
          color: var(--text, #18181b);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.6em;
          margin: 0;
        }

        /* Description */
        .pot-castle-product-description {
          font-size: clamp(0.8rem, 2vw, 0.875rem);
          color: var(--text-light, #52525b);
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin: 0;
        }

        /* Chips */
        .pot-castle-product-chips {
          display: flex;
          flex-wrap: wrap;
          gap: clamp(4px, 1vw, 8px);
          margin-top: 4px;
        }

        .pot-castle-chip {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: clamp(0.7rem, 1.5vw, 0.75rem);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pot-castle-chip-thc {
          background: rgba(139, 92, 246, 0.1);
          color: #7c3aed;
        }

        .pot-castle-chip-weight {
          background: rgba(0, 0, 0, 0.05);
          color: #52525b;
        }

        /* Price Options */
        .pot-castle-price-options {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .pot-castle-options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 70px), 1fr));
          gap: clamp(4px, 1vw, 8px);
        }

        .pot-castle-option-btn {
          padding: clamp(6px, 1.5vw, 8px) clamp(2px, 1vw, 4px);
          font-size: clamp(0.65rem, 1.5vw, 0.75rem);
          border-radius: 6px;
          border: 1px solid var(--border, #e4e4e7);
          background: var(--background, #ffffff);
          color: var(--text, #18181b);
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 600;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          line-height: 1.2;
          width: 100%;
        }

        .pot-castle-option-btn.active {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec489a 100%);
          color: white;
          border-color: transparent;
        }

        .pot-castle-option-price {
          font-size: 0.6rem;
          opacity: 0.8;
          margin-top: 2px;
        }

        .pot-castle-more-options {
          font-size: 0.7rem;
          color: var(--text-light, #52525b);
          text-align: center;
        }

        /* Footer */
        .pot-castle-product-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: clamp(8px, 2vw, 12px);
          margin-top: auto;
          padding-top: 8px;
          border-top: 1px solid var(--border, #e4e4e7);
        }

        .pot-castle-price-section {
          min-width: 0;
        }

        .pot-castle-price {
          font-size: clamp(1.125rem, 4vw, 1.25rem);
          font-weight: bold;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec489a 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .pot-castle-price-option {
          font-size: clamp(0.65rem, 1.5vw, 0.7rem);
          color: var(--text-light, #52525b);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .pot-castle-add-btn {
          padding: clamp(8px, 2vw, 10px) clamp(12px, 3vw, 16px);
          font-size: clamp(0.8rem, 2vw, 0.875rem);
          font-weight: 600;
          border-radius: 0.5rem;
          border: none;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec489a 100%);
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: clamp(90px, 20vw, 110px);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .pot-castle-add-btn:hover:not(.adding) {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .pot-castle-add-btn.adding {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .pot-castle-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: pot-castle-spin 1s linear infinite;
        }

        @keyframes pot-castle-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile Optimization */
        @media (max-width: 480px) {
          .pot-castle-product-image {
            padding-top: 70%;
          }
        }

        /* Touch Device Optimization */
        @media (hover: none) {
          .pot-castle-product-card:hover {
            transform: none;
          }
          
          .pot-castle-quick-view {
            display: none;
          }
          
          .pot-castle-add-btn:active {
            transform: scale(0.98);
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .pot-castle-product-card {
            background: #1f1f1f;
            border-color: rgba(139, 92, 246, 0.2);
          }
          
          .pot-castle-product-name {
            color: #fafafa;
          }
          
          .pot-castle-product-description {
            color: #a3a3a3;
          }
          
          .pot-castle-category {
            background: rgba(139, 92, 246, 0.2);
          }
          
          .pot-castle-rating {
            background: rgba(255, 255, 255, 0.1);
          }
          
          .pot-castle-chip-weight {
            background: rgba(255, 255, 255, 0.1);
            color: #d4d4d8;
          }
          
          .pot-castle-option-btn {
            background: #2d2d2d;
            border-color: #3f3f46;
            color: #fafafa;
          }
          
          .pot-castle-option-btn.active {
            background: linear-gradient(135deg, #8b5cf6 0%, #ec489a 100%);
          }
          
          .pot-castle-product-footer {
            border-top-color: #3f3f46;
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          .pot-castle-product-card,
          .pot-castle-product-img,
          .pot-castle-placeholder-emoji,
          .pot-castle-add-btn {
            transition: none;
          }
          
          .pot-castle-spinner {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;